import { useCallback, useState } from "react";
import type { GameHistoryNoteResponse } from "libs/apiClient";
import { useUserData } from "features/userData/context";
import { _ } from "i18n";
import { GameHistoryNoteCard } from "./GameHistoryNoteCard";
import { GameHistoryAddNoteForm } from "./GameHistoryAddNoteForm";
import { GameHistoryLogCard } from "./GameHistoryLogCard";
import {
	type GameHistoryApiType,
	useDiscoverGameHistoryFeed,
} from "./useDiscoverGameHistoryFeed";
import { GameHistoryNoteItemType } from "./constants";
import type { GameHistoryI18nKey } from "./gameHistorySubTabConfig";
import { usePendingFilesList } from "./gameHistoryPendingFiles";
import { useGameHistoryNoteMutations } from "./useGameHistoryNoteMutations";
import { useGameHistoryNoteEdit } from "./useGameHistoryNoteEdit";

interface GameHistoryFeedProps {
	gameId: number;
	apiType: GameHistoryApiType;
	showAddForm: boolean;
	emptyMessageKey: GameHistoryI18nKey;
	gameStatusLabels: Record<string, string>;
}

export function GameHistoryFeed({
	gameId,
	apiType,
	showAddForm,
	emptyMessageKey,
	gameStatusLabels,
}: GameHistoryFeedProps) {
	const user = useUserData();
	const [noteText, setNoteText] = useState("");
	const attachedFiles = usePendingFilesList();
	const noteEdit = useGameHistoryNoteEdit();

	const {
		isLoading,
		allItems,
		isLoadingMore,
		resetLoadedOlderPages,
		handleScroll,
		scrollFeedToBottom,
		feedRef,
	} = useDiscoverGameHistoryFeed(gameId, apiType);

	const { postMutation, patchMutation, deleteMutation } = useGameHistoryNoteMutations({
		gameId,
		resetLoadedOlderPages,
		scrollFeedToBottom,
		onPostSuccess: () => {
			setNoteText("");
			attachedFiles.clear();
		},
		onPatchSuccess: noteEdit.reset,
	});

	const handleSubmit = useCallback(() => {
		// Allow submitting a file-only entry (no text) as long as something is attached.
		if (!noteText.trim() && attachedFiles.files.length === 0) {
			return;
		}
		postMutation.mutate({
			id: gameId,
			body: { note: noteText, files: attachedFiles.files.map((f) => f.base64) },
		});
	}, [noteText, attachedFiles.files, gameId, postMutation]);

	const handleDelete = useCallback(
		(noteId: number) => {
			if (window.confirm(_("discover.gameDetail.history.confirmDelete"))) {
				deleteMutation.mutate({ noteId });
			}
		},
		[deleteMutation],
	);

	const renderItem = (item: GameHistoryNoteResponse) => {
		if (item.type === GameHistoryNoteItemType.Log) {
			return <GameHistoryLogCard key={item.id} item={item} gameStatusLabels={gameStatusLabels} />;
		}

		return (
			<GameHistoryNoteCard
				key={item.id}
				note={item}
				isOwner={item.createdBy?.id === user.id}
				isEditing={noteEdit.editingNoteId === item.id}
				editText={noteEdit.editText}
				editDeletedFileIds={noteEdit.editDeletedFileIds}
				editNewFiles={noteEdit.editNewFiles.files}
				isSaving={patchMutation.isLoading}
				onEdit={() => noteEdit.startEdit(item)}
				onDelete={() => handleDelete(item.id)}
				onEditTextChange={noteEdit.setEditText}
				onRemoveExistingFile={noteEdit.removeExistingFile}
				onEditFileDrop={noteEdit.editNewFiles.onFileDrop}
				onRemoveNewFile={noteEdit.editNewFiles.removeFile}
				onEditSave={() => {
					if (noteEdit.editText.trim()) {
						patchMutation.mutate({
							noteId: item.id,
							body: {
								note: noteEdit.editText,
								files: noteEdit.editNewFiles.files.map((f) => f.base64),
								deletedFileIds: noteEdit.editDeletedFileIds,
							},
						});
					}
				}}
				onEditCancel={noteEdit.reset}
			/>
		);
	};

	if (isLoading) {
		return <p className="discoverGameHistory-loading">{_("discover.gameDetail.history.loading")}</p>;
	}

	return (
		<>
			<div className="discoverGameHistory-feed" ref={feedRef} onScroll={handleScroll}>
				{isLoadingMore === true && (
					<div className="discoverGameHistory-loadingMore">
						{_("discover.gameDetail.history.loadingOlder")}
					</div>
				)}
				{allItems.length === 0 && (
					<p className="discoverGameHistory-empty">{_(emptyMessageKey)}</p>
				)}
				{allItems.map(renderItem)}
			</div>

			{showAddForm && (
				<GameHistoryAddNoteForm
					noteText={noteText}
					attachedFiles={attachedFiles.files}
					isSubmitting={postMutation.isLoading}
					userPhoto={user.photo}
					onNoteTextChange={setNoteText}
					onSubmit={handleSubmit}
					onFileDrop={attachedFiles.onFileDrop}
					onRemoveFile={attachedFiles.removeFile}
				/>
			)}
		</>
	);
}
